// import createPersistedState from "vuex-persistedstate";
import { createStore } from "vuex";
import processesModule from "./modules/processes";
import portfoliosModule from "./modules/portfolios";
import languagesModule from "./modules/languages";
import valueDriversModule from "./modules/value-drivers";
import staticContentModule from "./modules/static-content";
import hotspotsModule from "./modules/hotspots";
import { getLayers } from "@/services/api.service";
import { FILTER_CONFIG } from "@/constants/filter.constants";

const FALLBACK_LAYERS = [
	{ id: "facility-zones", layer_id: 1, name: "Facility Zones" },
	{ id: "process-flow", layer_id: 2, name: "Process Flow" },
	{ id: "key-solutions", layer_id: 3, name: "Key Solutions" },
];

const LAYER_ID_TO_SLUG = Object.fromEntries(
	FALLBACK_LAYERS.map((l) => [l.layer_id, l.id])
);

function normalizeLayer(apiLayer) {
	const rawSlug = String(apiLayer.id ?? "").toLowerCase().replace(/\s+/g, "-");
	const slug =
		apiLayer.slug ?? LAYER_ID_TO_SLUG[apiLayer.id] ?? rawSlug;
	const name = apiLayer.title ?? apiLayer.name ?? (LAYER_ID_TO_SLUG[apiLayer.id] ?? slug).replace(/-/g, " ");
	return {
		id: slug,
		layer_id: apiLayer.id,
		name,
	};
}

function payloadToLayerSlug(payload) {
	const str = String(payload ?? "").trim().toLowerCase();
	const num = Number(str);
	if (Number.isInteger(num) && LAYER_ID_TO_SLUG[num]) {
		return LAYER_ID_TO_SLUG[num];
	}
	if (str === "solution" || str === "solutions") return FILTER_CONFIG.SOLUTIONS_LAYER_ID;
	if (str === "process") return FILTER_CONFIG.FACILITY_ZONES_LAYER_ID;
	if (str === "process-equipment") return FILTER_CONFIG.PROCESS_FLOW_LAYER_ID;
	return str.replace(/\s+/g, "-") || FILTER_CONFIG.FACILITY_ZONES_LAYER_ID;
}

const store = createStore({
	modules: {
		processes: processesModule,
		portfolios: portfoliosModule,
		languages: languagesModule,
		valueDrivers: valueDriversModule,
		hotspots: hotspotsModule,
		staticContent: staticContentModule,
	},
	state() {
		return {
			layers: [...FALLBACK_LAYERS],
			activeLayer: { id: FILTER_CONFIG.FACILITY_ZONES_LAYER_ID, name: "Facility Zones" },
			defaultLayer: FILTER_CONFIG.FACILITY_ZONES_LAYER_ID,
		};
	},
	getters: {
		layers(state) {
			return state.layers;
		},
		activeLayer(state) {
			return state.activeLayer;
		},
		defaultLayer(state) {
			return state.defaultLayer;
		},
	},
	mutations: {
		setActiveLayer(state, payload) {
			state.activeLayer = payload;
		},
		setDefaultLayer(state, payload) {
			state.defaultLayer = payload;
		},
		setLayers(state, payload) {
			state.layers = Array.isArray(payload) && payload.length > 0 ? payload : [...FALLBACK_LAYERS];
		},
	},
	actions: {
		setActiveLayer(context, payload) {
			context.commit("setActiveLayer", payload);
		},
		setDefaultLayer(context, payload) {
			const layerId = payloadToLayerSlug(payload);
			context.commit("setDefaultLayer", layerId);
		},
		async fetchLayers(context) {
			try {
				const data = await getLayers();
				const list = Array.isArray(data) ? data : (data?.data ?? data?.layers ?? []);
				const normalized = list.map(normalizeLayer);
				context.commit("setLayers", normalized);
			} catch {
				context.commit("setLayers", []);
			}
		},
	},
	// plugins: [createPersistedState()],
});

export default store;
